import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa'; 
import '../styles/TelanganaPage.css'; 
import ClientBranches from './ClientBranches';
import CompanyProfile1 from './CompanyProfile1';

const TelanganaClientsPage = () => {
  const [visibleCards, setVisibleCards] = useState(0);
  const [counters, setCounters] = useState({
    clients: 0,
    peopleServed: 0,
    locations: 0
  });

  const tlcpHeroTitleRef = useRef(null);
  const tlcpHeroBreadcrumbRef = useRef(null);

  // Telangana specific client data
  const telanganaClients = [
    { name: "ROCK WOOL INDIA LTD.INDIA", location: "Hyderabad", persons: 600, logo: "/clients/rockwool-11.png", industry: "Manufacturing" }
  ];

  // Calculate total people served and unique locations for Telangana
  const totalPeopleServed = telanganaClients.reduce((sum, client) => sum + client.persons, 0);
  const totalLocations = [...new Set(telanganaClients.map(client => client.location))].length;

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          clients: Math.floor(telanganaClients.length * progress),
          peopleServed: Math.floor(totalPeopleServed * progress),
          locations: Math.floor(totalLocations * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            clients: telanganaClients.length,
            peopleServed: totalPeopleServed,
            locations: totalLocations
          });
        }
      }, stepTime);
    };
    animateCounters();
  }, [telanganaClients.length, totalPeopleServed, totalLocations]);

  // Animate client cards appearing in a staggered manner
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visibleCards < telanganaClients.length) {
        setVisibleCards(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [visibleCards, telanganaClients.length]);

  // Callback for Intersection Observer to add/remove animation class
  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("tlcp-animate-in");
      } else {
        entry.target.classList.remove("tlcp-animate-in");
      }
    });
  }, []);

  // Set up Intersection Observers for hero text elements
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentHeroTitleRef = tlcpHeroTitleRef.current;
    const currentHeroBreadcrumbRef = tlcpHeroBreadcrumbRef.current;

    if (currentHeroTitleRef) observer.observe(currentHeroTitleRef);
    if (currentHeroBreadcrumbRef) observer.observe(currentHeroBreadcrumbRef);

    return () => {
      if (currentHeroTitleRef) observer.unobserve(currentHeroTitleRef);
      if (currentHeroBreadcrumbRef) observer.unobserve(currentHeroBreadcrumbRef);
    };
  }, [handleIntersect]);

  return (
    <>
   <Helmet>
      <title>Clients in Telangana | Trusted Partner Rock Wool India Ltd. | Laxmi Hospitality Services</title>
      <meta
        name="description"
        content="Laxmi Hospitality Services proudly partners with Rock Wool India Ltd. in Hyderabad, Telangana, delivering trusted industrial catering services serving over 600 people with excellence and care."
      />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href="https://laxmihospitalityservices.in/clients/telangana" />
    
      {/* -------- OPEN GRAPH (SOCIAL) -------- */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://laxmihospitalityservices.in/clients/telangana" />
      <meta property="og:title" content="Clients in Telangana | Trusted Partner Rock Wool India Ltd. | Laxmi Hospitality Services" />
      <meta property="og:description" content="Strategic partnership in Telangana's tech hub. Serving Rock Wool India Ltd. in Hyderabad with 600+ people daily through exceptional industrial catering." />
      <meta property="og:site_name" content="Laxmi Hospitality Services" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:image" content="https://laxmihospitalityservices.in/images/Telangana.png" />
    
      {/* -------- TELANGANA CLIENTS COLLECTION PAGE -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": "https://laxmihospitalityservices.in/clients/telangana",
            "name": "Telangana Clients - Laxmi Hospitality Services",
            "description": "Industrial catering partnership in Telangana - serving manufacturing sector in Hyderabad with specialized food service solutions.",
            "url": "https://laxmihospitalityservices.in/clients/telangana",
            "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "isPartOf": {
              "@type": "WebPage",
              "@id": "https://laxmihospitalityservices.in/clients",
              "name": "Our Clients Across India"
            },
            "mainEntity": {
              "@type": "ItemList",
              "name": "Telangana Industrial Client",
              "description": "Strategic manufacturing partnership in Telangana's technology hub",
              "numberOfItems": 1,
              "itemListElement": [
                {
                  "@type": "Organization",
                  "name": "Rock Wool India Ltd.",
                  "description": "Leading manufacturing company in Hyderabad serving 600+ people with specialized industrial solutions",
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Hyderabad",
                    "addressRegion": "Telangana",
                    "addressCountry": "IN"
                  },
                  "numberOfEmployees": {
                    "@type": "QuantitativeValue",
                    "value": 25
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.linkedin.com/company/laxmi-hospitality-services/",
              "https://www.instagram.com/laxmi_hospitality_services/"
            ],
            "inLanguage": "en-IN"
          }
        `}
      </script>
    
      {/* -------- STATE-SPECIFIC BREADCRUMB -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": "https://laxmihospitalityservices.in/#breadcrumb",
            "name": "Website Breadcrumb",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",      "item": "https://laxmihospitalityservices.in/" },
              { "@type": "ListItem", "position": 2, "name": "Clients",   "item": "https://laxmihospitalityservices.in/clients" },
              { "@type": "ListItem", "position": 3, "name": "Telangana", "item": "https://laxmihospitalityservices.in/clients/telangana" }
            ]
          }
        `}
      </script>
    </Helmet>

    <div className="tlcp-page-container">
      <section className="tlcp-hero-section">
        <img
          src="/images/Telangana.png" 
          alt="Telangana Industry Landscape"
          className="tlcp-hero-image"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x600/f0f0f0/333333?text=Telangana+Hero"; }}
        />
        <div className="tlcp-hero-overlay">
          <h2 ref={tlcpHeroTitleRef} className="tlcp-hero-title">Our Clients in Telangana</h2>
          <p ref={tlcpHeroBreadcrumbRef} className="tlcp-breadcrumb">
            <a href="/" className="tlcp-breadcrumb-link">Home</a> /
            <a href="/clients" className="tlcp-breadcrumb-link"> Clients</a> / Telangana
          </p>
        </div>
      </section>

      <section className="tlcp-intro-stats-section">
        <div className="tlcp-container">
          <p className="tlcp-intro-stats-text">
            In the technologically advanced state of Telangana,  our significant partnership is with Rock Wool India Ltd. India, where we proudly contribute to their success and serve their valued workforce.
          </p>
        </div>
      </section>

      <section className="tlcp-stats-section">
        <div className="tlcp-container">
          <div className="tlcp-stats-grid">
            <div className="tlcp-stat-card">
              <div className="tlcp-stat-number">{counters.clients}</div>
              <div className="tlcp-stat-label">Clients</div>
            </div>
            <div className="tlcp-stat-card">
              <div className="tlcp-stat-number">{counters.peopleServed.toLocaleString()}</div>
              <div className="tlcp-stat-label">People Serving</div>
            </div>
            <div className="tlcp-stat-card">
              <div className="tlcp-stat-number">{counters.locations}</div>
              <div className="tlcp-stat-label">Locations</div>
            </div>
          </div>
        </div>
      </section>

      <section className="tlcp-clients-section">
        <div className="tlcp-container">
          <h2 className="tlcp-clients-section-title">Our Valued Clients</h2>
          <p className="tlcp-clients-section-intro">
        In Telangana, our commitment to fostering innovation and success is best exemplified through our strong partnership with Rock Wool India Ltd. India, a leading entity whose journey we are proud to support.
          </p>
          <div className="tlcp-clients-grid"> {/* This will contain only one card */}
            {telanganaClients.map((client, index) => (
              <div
                key={index}
                className={`tlcp-client-card ${index < visibleCards ? 'tlcp-visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Client Logo at the Top - Prominent */}
                <div className="tlcp-card-logo-wrapper">
                  <img
                    src={client.logo}
                    alt={`${client.name} Logo`}
                    className="tlcp-card-logo-image"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/150x100/e0e0e0/333333?text=${client.name.split(' ')[0]}`; }}
                  />
                </div>

                {/* Client Info below logo */}
                <div className="tlcp-client-info-v2">
                  <h3 className="tlcp-client-name-v2">{client.name}</h3>
                  <div className="tlcp-location-v2">
                    <FaMapMarkerAlt className="tlcp-icon-v2" />
                    {client.location}
                  </div>
                  <div className="tlcp-people-served">
                    <FaUsers className="tlcp-icon-v2" />
                    {client.persons.toLocaleString()} People Serving
                  </div>
                  <div className="tlcp-type-tag">
                    Type: {client.industry}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ClientBranches/>
      <CompanyProfile1/>
    </div>
    </>
  );
};

export default TelanganaClientsPage;
